import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { router } from "@inertiajs/react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, type SelectOption } from "@/components/ui/select"
import { TypeBadge } from "@/components/domain/custom-field-row"
import { useCan } from "@/lib/authz"
import { cn } from "@/lib/cn"
import { CHOICE_COLORS, choiceColorClasses } from "@/lib/choice-colors"
import type {
  CustomFieldDefinition,
  CustomFieldParentType,
  CustomFieldType,
  CustomFieldStatus,
} from "@/types/custom_field"
import type { InertiaFormValue } from "@/types/inertia"

// Right-edge slide-over for editing or creating a CustomFieldDefinition.
// Renders inline (the parent positions a fixed/right shell around it).
//
// Modes:
//   - edit:   row exists. Pending rows become Promote on save; live rows
//             save through PATCH. The Type select is disabled on live /
//             archived rows — retyping would require scanning every
//             parent jsonb (potentially millions of rows) and silently
//             corrupt downstream consumers, so we lock it. To change a
//             type the operator archives the field and creates a new key.
//   - create: empty form posting a brand-new live definition. The
//             operator sets `key` (immutable post-create), label, type,
//             AI flags, etc.
//
// Tabs:
//   - General: core attributes + AI access
//   - Options: per-type config (choices for select, min/max for
//             number/date)
//   - Samples: pending-only — up to 100 actual partner-pushed values
//             for this key, lazily fetched, so the operator can
//             review real data before classifying.
//
// Admin gating: AI access toggles (llm_visible/llm_settable/pii) are
// disabled for operators without `custom_fields_llm_toggle`.

type Tab = "general" | "options" | "samples"
type Mode = "edit" | "create"

const TYPE_OPTIONS: SelectOption<CustomFieldType>[] = [
  { value: "text",         label: "Text" },
  { value: "number",       label: "Number" },
  { value: "boolean",      label: "Checkbox" },
  { value: "date",         label: "Date" },
  { value: "select",       label: "Dropdown" },
  { value: "multi_select", label: "Multi-select" },
]

const STATUS_OPTIONS: SelectOption<CustomFieldStatus>[] = [
  { value: "live",     label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "pending",  label: "Pending" },
]

// Stock Tailwind colors so the histogram + legend dots always render
// regardless of which custom design tokens are wired into the build.
const TYPE_BUCKETS: Array<{ key: keyof typeof STATS_LABEL; color: string }> = [
  { key: "text",         color: "bg-sky-500" },
  { key: "number",       color: "bg-blue-500" },
  { key: "boolean",      color: "bg-emerald-500" },
  { key: "date",         color: "bg-amber-500" },
  { key: "multi_select", color: "bg-pink-500" },
]
const STATS_LABEL = {
  text: "text", number: "number", boolean: "boolean",
  date: "date", multi_select: "array",
} as const

// Type subtitle copy used below the field name in the drawer header.
// Truncation at 2 KB (RenderForLlm::MAX_VALUE_LEN) keeps long-form
// values bounded so a single field can't dominate the prompt budget.
const TYPE_DESCRIPTION: Record<CustomFieldType, string> = {
  text:         "Free-form text (long values truncated at 2 KB in the AI prompt)",
  number:       "Numeric input with optional min/max",
  boolean:      "On/off toggle",
  date:         "Calendar date picker",
  select:       "Select one option from a list",
  multi_select: "Select multiple options from a list",
}

const TYPE_LABEL: Record<CustomFieldType, string> = {
  text:         "Text",
  number:       "Number",
  boolean:      "Checkbox",
  date:         "Date",
  select:       "Dropdown",
  multi_select: "Multi-select",
}

type SourceStateValetStatus = "active" | "banned" | "locked" | "ignored"

const VALET_STATUS_OPTIONS: SelectOption<SourceStateValetStatus | "">[] = [
  { value: "",        label: "Auto" },
  { value: "active",  label: "Active" },
  { value: "banned",  label: "Banned / blocked" },
  { value: "locked",  label: "Temporarily locked" },
  { value: "ignored", label: "No blocked-user handling" },
]

const SOURCE_STATE_BANNED_WORDS = new Set([
  "banned", "ban", "blocked", "block", "denied", "deny", "disabled",
  "suspended", "deactivated", "terminated", "restricted", "ineligible",
  "fraud", "fraudulent",
])
const SOURCE_STATE_LOCKED_WORDS = new Set([
  "locked", "lockout", "locked_out", "paused", "pause", "hold", "on_hold",
  "temporary_hold",
])
const SOURCE_STATE_ACTIVE_WORDS = new Set([
  "active", "enabled", "good", "ok", "okay", "clear", "open", "current",
  "vip", "regular", "normal", "trial",
])

// In-memory editable copy. Anything not here is fixed (key after create,
// parent_type) or computed (inferredTypeStats).
type DraftState = {
  key: string
  label: string
  description: string
  fieldType: CustomFieldType
  position: number
  status: CustomFieldStatus
  options: CustomFieldDefinition["options"]
  llmVisible: boolean
  llmSettable: boolean
  pii: boolean
  reserved: boolean
}

function buildDraft(d?: CustomFieldDefinition): DraftState {
  return {
    key:         d?.key ?? "",
    label:       d?.label ?? "",
    description: d?.description ?? "",
    fieldType:   d?.fieldType ?? "text",
    position:    d?.position ?? 0,
    status:      d?.status ?? "live",
    options:     d?.options ?? {},
    llmVisible:  d?.llmVisible ?? false,
    llmSettable: d?.llmSettable ?? false,
    pii:         d?.pii ?? false,
    reserved:    d?.reserved ?? false,
  }
}

type Props = {
  mode: Mode
  // Required when mode === "edit". Absent in create mode.
  definition?: CustomFieldDefinition
  // Required when mode === "create" — picks which parent_type the new
  // row belongs to. Ignored in edit mode (the row already has one).
  parentType: CustomFieldParentType
  onClose: () => void
}

export function CustomFieldDrawer({ mode, definition, parentType, onClose }: Props) {
  const can = useCan()
  const llmAdmin = can("custom_fields_llm_toggle")
  const isCreate = mode === "create"

  const [tab, setTab] = useState<Tab>("general")
  const [draft, setDraft] = useState<DraftState>(() => buildDraft(definition))
  const [submitting, setSubmitting] = useState(false)

  // PII confirm modal — pops when the operator flips llm_visible on a
  // PII-flagged field for the first time.
  const [confirmingPiiVisible, setConfirmingPiiVisible] = useState(false)

  // Reset draft when the parent passes a new row or refreshes.
  useEffect(() => {
    setDraft(buildDraft(definition))
    setTab("general")
    setConfirmingPiiVisible(false)
  }, [definition?.id, definition?.updatedAt, mode])

  const isPending  = !isCreate && definition?.status === "pending"
  const isArchived = !isCreate && definition?.status === "archived"

  const optionsTabApplicable = useMemo(() => {
    return ["select", "multi_select", "number", "date"].includes(draft.fieldType)
  }, [draft.fieldType])

  const setField = <K extends keyof DraftState>(k: K, v: DraftState[K]) => {
    setDraft((d) => ({ ...d, [k]: v }))
  }

  // True when flipping llm_visible to ON would put PII into the system
  // prompt. Triggers the confirm modal before the actual save fires.
  const wouldExposePii = !isCreate &&
    draft.pii && draft.llmVisible && !definition?.llmVisible

  const submit = () => {
    if (submitting) return

    if (wouldExposePii) {
      setConfirmingPiiVisible(true)
      return
    }

    if (isCreate) {
      doCreate()
      return
    }

    // Pending and live both go through the same update path. To
    // promote a pending row, the operator changes the Status select
    // to "Active" and clicks Save — there's no auto-promote magic.
    doVanillaUpdate()
  }

  const doCreate = () => {
    if (!draft.key.trim() || !draft.label.trim()) {
      alert("Field name and key are required.")
      return
    }
    setSubmitting(true)
    router.post(
      "/custom_fields",
      {
        custom_field_definition: {
          parent_type:  parentType,
          key:          draft.key.trim(),
          label:        draft.label,
          description:  draft.description,
          field_type:   draft.fieldType,
          options:      draft.options,
          llm_visible:  draft.llmVisible,
          llm_settable: draft.llmSettable,
          pii:          draft.pii,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => onClose(),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  const doVanillaUpdate = () => {
    if (!definition) return
    setSubmitting(true)
    router.patch(
      `/custom_fields/${definition.id}`,
      {
        custom_field_definition: {
          label:        draft.label,
          description:  draft.description,
          position:     draft.position,
          // field_type is sent on every save — backend rejects changes
          // when the row is already :live (immutable post-active).
          field_type:   draft.fieldType,
          options:      draft.options,
          llm_visible:  draft.llmVisible,
          llm_settable: draft.llmSettable,
          pii:          draft.pii,
          status:       draft.status,
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
      },
    )
  }

  // Header type icon — derives from the draft so changing the type in
  // the form updates the header preview instantly.
  const headerType = isCreate ? draft.fieldType : (definition?.fieldType ?? draft.fieldType)

  return (
    <div className="flex h-full flex-col bg-app-panel border-l border-border">
      {/* Header */}
      <header className="flex items-start gap-3 border-b border-border px-5 py-4">
        <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-control", typeIconBg(headerType), typeIconFg(headerType))}>
          <span className="font-mono text-xs font-semibold">{TYPE_GLYPH[headerType]}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-semibold text-app-text">
            {isCreate ? "New custom field" : (draft.label || "Untitled field")}
          </div>
          <div className="text-[11px] text-app-muted">
            {TYPE_LABEL[headerType]}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-control p-1 text-app-muted hover:bg-white/5 hover:text-app-text"
        >
          <X size={18} />
        </button>
      </header>

      {/* Tabs */}
      <nav className="flex border-b border-border px-2">
        <TabButton active={tab === "general"} onClick={() => setTab("general")}>General</TabButton>
        {optionsTabApplicable && (
          <TabButton active={tab === "options"} onClick={() => setTab("options")}>Options</TabButton>
        )}
        {/* Samples tab — pending rows only. Lets the operator review
            actual partner data before classifying. Hidden once the row
            goes live (operator has all the context they need). */}
        {isPending && definition && (
          <TabButton active={tab === "samples"} onClick={() => setTab("samples")}>Samples</TabButton>
        )}
      </nav>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {tab === "general" && (
          <GeneralTab
            isCreate={isCreate}
            definition={definition}
            draft={draft}
            setField={setField}
            llmAdmin={llmAdmin}
          />
        )}
        {tab === "options" && (
          <OptionsTab draft={draft} parentType={isCreate ? parentType : (definition?.parentType ?? parentType)} setField={setField} />
        )}
        {tab === "samples" && definition && (
          <SamplesTab definitionId={definition.id} fieldKey={definition.key} />
        )}
      </div>

      {/* Footer */}
      <footer className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
        {isArchived && (
          <span className="mr-2 text-[11px] italic text-app-muted">
            Read-only — archived rows can't be edited.
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-control border border-border bg-white/5 px-3 py-1.5 text-xs text-app-text transition hover:bg-white/10"
        >
          Cancel
        </button>
        {!isArchived && (
          <Button size="sm" onClick={submit} disabled={submitting}>
            {isCreate
              ? (submitting ? "Creating…" : "Create field")
              : (submitting ? "Saving…" : "Save changes")}
          </Button>
        )}
      </footer>

      {/* PII + llm_visible double-confirm modal */}
      {confirmingPiiVisible && (
        <PiiVisibleConfirmModal
          onCancel={() => {
            setConfirmingPiiVisible(false)
            setField("llmVisible", false)
          }}
          onConfirm={() => {
            setConfirmingPiiVisible(false)
            doVanillaUpdate()
          }}
        />
      )}
    </div>
  )
}

const TYPE_GLYPH: Record<CustomFieldType, string> = {
  text: "Aa", number: "123", boolean: "✓",
  date: "▦", select: "▾", multi_select: "▾▾",
}
const typeIconBg = (t: CustomFieldType) => ({
  text: "bg-sky-500/15", number: "bg-blue-500/15",
  boolean: "bg-status-success/15", date: "bg-status-warning/15",
  select: "bg-violet-500/15", multi_select: "bg-pink-500/15",
}[t])
const typeIconFg = (t: CustomFieldType) => ({
  text: "text-sky-300", number: "text-blue-300",
  boolean: "text-status-success", date: "text-status-warning",
  select: "text-violet-300", multi_select: "text-pink-300",
}[t])

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-2.5 text-xs font-medium border-b-2 transition",
        active
          ? "border-brand-red text-brand-red"
          : "border-transparent text-app-muted hover:text-app-text",
      )}
    >
      {children}
    </button>
  )
}

function GeneralTab({
  isCreate,
  definition,
  draft,
  setField,
  llmAdmin,
}: {
  isCreate: boolean
  definition?: CustomFieldDefinition
  draft: DraftState
  setField: <K extends keyof DraftState>(k: K, v: DraftState[K]) => void
  llmAdmin: boolean
}) {
  const isPending = !isCreate && definition?.status === "pending"
  // Type is settable at creation and on pending rows (where it's part
  // of the promote-to-live decision). Once a row is live (or
  // archived), the type is frozen — see the field comment above.
  const typeLocked = !isCreate && !isPending

  // Pre-populate `options.choices` from the partner-pushed distinct
  // values so the operator doesn't have to retype them. Existing
  // operator-supplied choices win — no overwrite if the editor already
  // has any.
  const applyChoicesFromSeen = (d: CustomFieldDefinition) => {
    const existing = Array.isArray(draft.options?.choices) ? (draft.options.choices as Array<{ value: string }>) : []
    if (existing.length > 0) return
    const choices = d.seenValues.map((v) => ({ value: v, label: v }))
    setField("options", { ...draft.options, choices })
  }

  return (
    <div className="space-y-5">
      {/* Pending detail panel — histogram (informational) + candidate
          choices (the only actionable block, when partner-pushed
          strings exist). The bare "Use suggestion" affordance was
          dropped because the def's field_type already equals the
          inferred suggestion at auto-pending creation time, so the
          button was usually a no-op. The Type select below is the
          general control; the candidate-choices buttons are the
          one-click promotion shortcut for select / multi_select. */}
      {isPending && definition && (
        <PendingInferencePanel
          definition={definition}
          onUseAsChoices={(targetType) => {
            setField("fieldType", targetType)
            // Pre-fill choices from seen_values only when promoting
            // to a dropdown — for primitive types (date / number /
            // boolean / text) there's nothing to fill.
            if (targetType === "select" || targetType === "multi_select") {
              applyChoicesFromSeen(definition)
            }
          }}
        />
      )}

      <FieldGroup label="Field name" hint="Displayed to agents">
        <Input
          type="text"
          value={draft.label}
          onChange={(e) => setField("label", e.target.value)}
          className="w-full"
          maxLength={80}
          placeholder="Priority Level"
        />
      </FieldGroup>

      <FieldGroup label="Field key" hint={isCreate ? "Lowercase, snake_case. Used in APIs and exports." : "Used in APIs and exports"}>
        {isCreate ? (
          <Input
            type="text"
            value={draft.key}
            onChange={(e) => setField("key", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
            className="w-full font-mono"
            maxLength={64}
            placeholder="priority_level"
          />
        ) : (
          <div className="flex items-center gap-2">
            <code className="flex-1 select-all rounded-control border border-border bg-app-panel/40 px-3 py-2 font-mono text-xs text-app-text">
              {definition?.key}
            </code>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(definition?.key ?? "")}
              className="rounded-control border border-border px-2 py-2 text-[11px] text-app-muted hover:bg-white/5"
              title="Copy key"
            >
              Copy
            </button>
          </div>
        )}
      </FieldGroup>

      <FieldGroup label="Description" hint="What does this field represent?">
        <Textarea
          rows={3}
          value={draft.description}
          onChange={(e) => setField("description", e.target.value)}
          maxLength={500}
          placeholder="Urgency of the request"
        />
      </FieldGroup>

      <FieldGroup label="Status" hint="Field is available to use">
        <Select<CustomFieldStatus>
          value={STATUS_OPTIONS.find((o) => o.value === draft.status) ?? null}
          onChange={(o) => o && setField("status", o.value)}
          options={isCreate ? STATUS_OPTIONS.filter((o) => o.value !== "pending") : STATUS_OPTIONS}
          isSearchable={false}
        />
      </FieldGroup>

      <FieldGroup
        label="Type"
        hint={typeLocked
          ? "Locked. Type can only be chosen at creation or during pending → live promotion. To change it, archive this field and create a new key."
          : undefined}
      >
        <Select<CustomFieldType>
          value={TYPE_OPTIONS.find((o) => o.value === draft.fieldType) ?? null}
          onChange={(o) => o && setField("fieldType", o.value)}
          options={TYPE_OPTIONS}
          isSearchable={false}
          isDisabled={typeLocked}
        />
        <div className="mt-2 flex items-center gap-3 rounded-control border border-border bg-white/[0.02] px-3 py-2.5">
          <TypeBadge type={draft.fieldType} size="md" />
          <div className="text-[11px] text-app-muted">{TYPE_DESCRIPTION[draft.fieldType]}</div>
        </div>
      </FieldGroup>

      {/* AI access — visually grouped to make the gate obvious. */}
      <div className="rounded-control border border-vip-gold/30 bg-vip-goldMute/40 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-vip-goldSoft">AI access</div>
          {!llmAdmin && (
            <span className="text-[11px] italic text-app-muted">Requires admin role</span>
          )}
        </div>
        <ToggleRow
          label="Visible to AI"
          hint="The AI sees this field's value in conversations."
          checked={draft.llmVisible}
          onChange={(v) => setField("llmVisible", v)}
          disabled={!llmAdmin}
        />
        <ToggleRow
          label="AI-writable"
          hint="The AI can write this field via tool calls. Avoid for billing, entitlement, or routing-critical fields."
          checked={draft.llmSettable}
          onChange={(v) => setField("llmSettable", v)}
          disabled={!llmAdmin}
        />
        <ToggleRow
          label="Contains PII"
          hint="Values are masked in the operator UI by default."
          checked={draft.pii}
          onChange={(v) => setField("pii", v)}
          disabled={!llmAdmin}
        />
      </div>

    </div>
  )
}

function OptionsTab({
  draft,
  parentType,
  setField,
}: {
  draft: DraftState
  parentType: CustomFieldParentType
  setField: <K extends keyof DraftState>(k: K, v: DraftState[K]) => void
}) {
  const setOption = (key: string, value: InertiaFormValue) => {
    setField("options", { ...draft.options, [key]: value })
  }

  if (draft.fieldType === "select" || draft.fieldType === "multi_select") {
    // strict_choices defaults to true (reject mismatches). Stored as
    // an explicit option so admins can opt into the auto-extend mode
    // by toggling it off.
    const strict = draft.options?.strict_choices !== false
    return (
      <div className="space-y-4">
        <ChoicesEditor
          choices={Array.isArray(draft.options.choices) ? (draft.options.choices as EditorChoice[]) : []}
          onChange={(next) => setOption("choices", next)}
          withColor={!!draft.reserved}
          withValetStatus={draft.reserved && draft.key === "source_state" && parentType === "appuser"}
        />
        <div className="rounded-control border border-border bg-app-panel/40 p-3 space-y-2">
          <ToggleRow
            label="Strict choices"
            hint={strict
              ? "Writes with values outside the list above are rejected. Recommended for billing tiers, entitlement flags, and any field where unexpected values would cause routing or downstream bugs."
              : "Writes with new values are accepted and auto-appended to the choices list. Useful for free-form tag clouds where partners legitimately introduce new categories over time."}
            checked={strict}
            onChange={(v) => setOption("strict_choices", v)}
          />
        </div>
      </div>
    )
  }

  if (draft.fieldType === "number") {
    return (
      <div className="space-y-3">
        <FieldGroup label="Min">
          <Input
            type="number"
            value={(draft.options.min as number) ?? ""}
            onChange={(e) => setOption("min", e.target.value === "" ? null : parseFloat(e.target.value))}
          />
        </FieldGroup>
        <FieldGroup label="Max">
          <Input
            type="number"
            value={(draft.options.max as number) ?? ""}
            onChange={(e) => setOption("max", e.target.value === "" ? null : parseFloat(e.target.value))}
          />
        </FieldGroup>
        <FieldGroup label="Precision (decimal places)">
          <Input
            type="number"
            min={0}
            max={10}
            value={(draft.options.precision as number) ?? ""}
            onChange={(e) => setOption("precision", e.target.value === "" ? null : parseInt(e.target.value, 10))}
          />
        </FieldGroup>
      </div>
    )
  }

  if (draft.fieldType === "date") {
    return (
      <div className="space-y-3">
        <FieldGroup label="Min date (ISO YYYY-MM-DD)">
          <Input
            type="text"
            value={(draft.options.min as string) ?? ""}
            onChange={(e) => setOption("min", e.target.value || null)}
            placeholder="2024-01-01"
          />
        </FieldGroup>
        <FieldGroup label="Max date (ISO YYYY-MM-DD)">
          <Input
            type="text"
            value={(draft.options.max as string) ?? ""}
            onChange={(e) => setOption("max", e.target.value || null)}
            placeholder="2030-12-31"
          />
        </FieldGroup>
      </div>
    )
  }

  return (
    <div className="text-xs text-app-muted">
      No options for this field type.
    </div>
  )
}

type SampleRow = { parent_id: number; parent_label: string | null; value: unknown }

function SamplesTab({
  definitionId,
  fieldKey,
}: {
  definitionId: number
  fieldKey: string
}) {
  const [loading, setLoading]      = useState(true)
  const [error, setError]          = useState<string | null>(null)
  const [rows, setRows]             = useState<SampleRow[]>([])
  const [total, setTotal]           = useState<number>(0)
  const [limit, setLimit]           = useState<number>(100)
  const [parentType, setParentType] = useState<string>("appuser")

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fetch(`/custom_fields/${definitionId}/samples`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((r) => r.json())
      .then((json) => {
        if (!alive) return
        if (json.status === "ok") {
          setRows(json.values ?? [])
          setTotal(json.total ?? 0)
          setLimit(json.limit ?? 100)
          setParentType(json.parent_type ?? "appuser")
        } else {
          setError(json.message ?? "Failed to load samples.")
        }
      })
      .catch((e) => {
        if (!alive) return
        setError(e instanceof Error ? e.message : "Failed to load samples.")
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [definitionId])

  if (loading) {
    return <div className="text-xs text-app-muted">Loading samples…</div>
  }
  if (error) {
    return <div className="rounded-control border border-status-error/30 bg-status-error/10 px-3 py-2 text-xs text-status-error">{error}</div>
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-border p-4 text-center text-xs text-app-muted">
        No values written yet. Once partners push values for
        <code className="mx-1 rounded bg-white/5 px-1 font-mono">{fieldKey}</code>,
        they'll appear here.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-[11px] text-app-muted">
        <span>
          Showing {rows.length} of {total.toLocaleString()} {total === 1 ? "value" : "values"}
          {total > limit && ` (capped at ${limit})`}
        </span>
        <span className="text-[10px]">most recent first</span>
      </div>
      <div className="overflow-hidden rounded-control border border-border">
        <table className="w-full text-[11px]">
          <thead className="bg-app-panel2/30 text-[10px] font-semibold uppercase tracking-wide text-app-muted">
            <tr>
              <th className="px-3 py-2 text-left">{parentType === "agent_convo" ? "Convo" : "Source key"}</th>
              <th className="px-3 py-2 text-left">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.parent_id}-${i}`} className="border-t border-border/60">
                <td className="px-3 py-1.5 align-top font-mono text-app-muted">
                  {r.parent_label ?? `#${r.parent_id}`}
                </td>
                <td className="px-3 py-1.5">
                  <code className="block break-all font-mono text-app-text">
                    {formatSampleValue(r.value)}
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function formatSampleValue(v: unknown): string {
  if (v === null || v === undefined) return "(null)"
  if (typeof v === "string") return v
  return JSON.stringify(v)
}

type EditorChoice = {
  value: string
  label: string
  color?: string | null
  valet_status?: SourceStateValetStatus | null
}

function ChoicesEditor({
  choices,
  onChange,
  withColor = false,
  withValetStatus = false,
}: {
  choices: EditorChoice[]
  onChange: (next: EditorChoice[]) => void
  withColor?: boolean
  withValetStatus?: boolean
}) {
  const update = <K extends keyof EditorChoice>(idx: number, key: K, val: EditorChoice[K]) => {
    const next = choices.slice()
    const edited = { ...next[idx], [key]: val }
    next[idx] = withValetStatus ? normalizeSourceStateChoice(edited, key === "valet_status") : edited
    onChange(next)
  }
  const remove = (idx: number) => {
    onChange(choices.filter((_, i) => i !== idx))
  }
  const add = () => {
    onChange([...choices, { value: "", label: "" }])
  }
  const gridCols =
    withValetStatus
      ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(150px,0.8fr)_44px_36px]"
      : withColor
        ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_44px_36px]"
        : "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_36px]"
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-app-muted">Choices</div>
      {withValetStatus && (
        <div className="rounded-control border border-border bg-app-panel/40 px-3 py-2 text-xs text-app-muted">
          Map each partner value to Valet's meaning. Banned or blocked meanings default to red when no color is selected.
        </div>
      )}
      {choices.length === 0 && (
        <div className="rounded-control border border-dashed border-border p-3 text-center text-xs text-app-muted">
          No choices yet. Add one to get started.
        </div>
      )}
      {choices.length > 0 && (
        <div className={`grid ${gridCols} items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-app-muted`}>
          <span title="API value — stable identifier sent in API writes and used for matching">Value</span>
          <span title="Operator-facing label — what humans see in the UI">Label</span>
          {withValetStatus && <span title="How Valet should interpret this partner state">Valet meaning</span>}
          {withColor && <span title="Display color used for the sys chip on the appuser detail page">Color</span>}
          <span />
        </div>
      )}
      {choices.map((rawChoice, i) => {
        const c = withValetStatus ? normalizeSourceStateChoice(rawChoice) : rawChoice
        return (
        <div key={i} className={`grid ${gridCols} items-center gap-2`}>
          <Input
            type="text"
            placeholder="value"
            value={c.value}
            onChange={(e) => update(i, "value", e.target.value)}
            className="font-mono"
          />
          <Input
            type="text"
            placeholder="Label"
            value={c.label}
            onChange={(e) => update(i, "label", e.target.value)}
          />
          {withValetStatus && (
            <Select<SourceStateValetStatus | "">
              value={VALET_STATUS_OPTIONS.find((o) => o.value === (c.valet_status ?? "")) ?? null}
              options={VALET_STATUS_OPTIONS}
              onChange={(option) => update(i, "valet_status", (option?.value || null) as EditorChoice["valet_status"])}
              placeholder="Auto"
              isSearchable={false}
            />
          )}
          {withColor && (
            <ColorPickerCell
              value={c.color ?? null}
              onChange={(next) => update(i, "color", next)}
            />
          )}
          <button
            type="button"
            onClick={() => remove(i)}
            className="rounded-control border border-border px-2 py-2 text-app-muted hover:bg-white/5"
            title="Remove"
          >
            ×
          </button>
        </div>
        )
      })}
      <button
        type="button"
        onClick={add}
        className="w-full rounded-control border border-dashed border-border py-2 text-xs text-app-muted transition hover:bg-white/5 hover:text-app-text"
      >
        + Add choice
      </button>
    </div>
  )
}

function normalizeSourceStateChoice(choice: EditorChoice, explicitStatus = false): EditorChoice {
  const inferred = inferSourceStateValetStatus(choice.value, choice.label)
  const status = explicitStatus ? choice.valet_status : (choice.valet_status || inferred)
  const next = { ...choice }
  if (status) next.valet_status = status
  if (status === "banned" && !next.color) next.color = "red"
  return next
}

function inferSourceStateValetStatus(value: string, label?: string | null): SourceStateValetStatus | null {
  const normalized = normalizeSourceStateToken(`${value ?? ""} ${label ?? ""}`)
  const tokens = normalized.split("_").filter(Boolean)
  if (tokens.some((t) => SOURCE_STATE_BANNED_WORDS.has(t)) || SOURCE_STATE_BANNED_WORDS.has(normalized)) return "banned"
  if (tokens.some((t) => SOURCE_STATE_LOCKED_WORDS.has(t)) || SOURCE_STATE_LOCKED_WORDS.has(normalized)) return "locked"
  if (tokens.some((t) => SOURCE_STATE_ACTIVE_WORDS.has(t)) || SOURCE_STATE_ACTIVE_WORDS.has(normalized)) return "active"
  return null
}

function normalizeSourceStateToken(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
}

function ColorPickerCell({
  value,
  onChange,
}: {
  value: string | null
  onChange: (next: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const classes = choiceColorClasses(value)

  // Render the popover into <body> via a portal so the drawer's overflow
  // can't clip it. Position is computed from the trigger button's
  // bounding rect each time it opens; closing on resize/scroll keeps it
  // from drifting since we don't recompute on the fly.
  const openPicker = () => {
    if (!buttonRef.current) return
    const r = buttonRef.current.getBoundingClientRect()
    // Anchor: drop below the trigger, right-align so wide popover grows leftward.
    setCoords({ top: r.bottom + 6, left: r.right - 240 /* approx popover width */ })
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onDocPointer = (e: MouseEvent) => {
      const t = e.target as Node
      if (popoverRef.current?.contains(t) || buttonRef.current?.contains(t)) return
      setOpen(false)
    }
    const onDismiss = () => setOpen(false)
    document.addEventListener("mousedown", onDocPointer)
    window.addEventListener("scroll", onDismiss, true)
    window.addEventListener("resize", onDismiss)
    return () => {
      document.removeEventListener("mousedown", onDocPointer)
      window.removeEventListener("scroll", onDismiss, true)
      window.removeEventListener("resize", onDismiss)
    }
  }, [open])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openPicker())}
        className="h-9 w-9 rounded-control border border-border hover:bg-white/5 flex items-center justify-center"
        title={value ? `Color: ${value}` : "Pick a color"}
      >
        <span
          className={`block h-4 w-4 rounded-full border border-border/60 ${classes ? classes.swatch : "bg-app-panel"}`}
        />
      </button>
      {open && coords && createPortal(
        <div
          ref={popoverRef}
          style={{ position: "fixed", top: coords.top, left: Math.max(8, coords.left) }}
          className="z-[1000] w-max min-w-[15rem] grid grid-cols-4 gap-2.5 rounded-control border border-border bg-app-panel p-3 shadow-2xl"
        >
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(false) }}
            className="h-9 w-9 rounded-full border border-dashed border-app-subtle/60 text-xs text-app-subtle hover:bg-white/5"
            title="No color (neutral)"
          >
            ×
          </button>
          {CHOICE_COLORS.map((c) => {
            const cl = choiceColorClasses(c)!
            const selected = value === c
            return (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setOpen(false) }}
                className={`h-9 w-9 rounded-full ${cl.swatch} border-2 ${selected ? "border-white ring-2 ring-white/40" : "border-border/40"} hover:scale-110 transition`}
                title={c}
              />
            )
          })}
        </div>,
        document.body
      )}
    </>
  )
}

function PendingInferencePanel({
  definition,
  onUseAsChoices,
}: {
  definition: CustomFieldDefinition
  // Operator clicked "Use as :select / :multi_select choices" — switch
  // the draft to that target type AND pre-populate options.choices
  // from seen_values in one click.
  onUseAsChoices: (target: CustomFieldType) => void
}) {
  const stats = definition.inferredTypeStats ?? {}
  const samples = stats.samples ?? 0
  const total = TYPE_BUCKETS.reduce((sum, b) => sum + (stats[b.key] ?? 0), 0) || 1

  const segments = TYPE_BUCKETS.map((b) => {
    const count = stats[b.key] ?? 0
    const pct = total > 0 ? (count / total) * 100 : 0
    return { ...b, count, pct }
  })

  const seen = definition.seenValues ?? []

  return (
    <div className="rounded-control border border-violet-500/30 bg-violet-500/5 p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-violet-300">
          Type recommendation
        </div>
        <div className="text-[11px] text-app-muted">
          {samples > 0 ? `after ${samples.toLocaleString()} sample${samples === 1 ? "" : "s"}` : "no samples yet"}
        </div>
      </div>

      <div className="overflow-hidden rounded-control border border-border bg-app-panel/40 h-6 flex">
        {segments.map((s) => (
          s.pct > 0 && (
            <div
              key={s.key}
              className={cn("h-full", s.color)}
              style={{ width: `${s.pct}%` }}
              title={`${s.key}: ${s.count} (${s.pct.toFixed(1)}%)`}
            />
          )
        ))}
        {samples === 0 && (
          <div className="flex h-full w-full items-center justify-center text-[11px] text-app-muted">
            no histogram data
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5 text-app-muted">
            <span className={cn("inline-block h-2 w-2 rounded-full", s.color)} />
            <span className="font-medium text-app-text">{STATS_LABEL[s.key]}</span>
            <span>{s.count} ({s.pct.toFixed(0)}%)</span>
          </div>
        ))}
      </div>

      {/* Action row + (optional) seen-values evidence. Primary button
          accepts the histogram-inferred suggestion ("Use as <type>");
          multi-select / single-select alternatives stay available so
          the operator can override when they know better. Dedup so we
          don't render "Use as Multi-select" twice when that's also
          the suggestion. */}
      <div className="space-y-2">
        {seen.length > 0 && (
          <div className="text-[10px] uppercase tracking-wide text-app-muted">
            {seen.length} distinct value{seen.length === 1 ? "" : "s"} seen
          </div>
        )}

        {seen.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {seen.slice(0, 24).map((v) => (
              <code key={v} className="rounded-chip border border-border bg-app-panel/40 px-1.5 py-0.5 font-mono text-[10px] text-app-text">
                {v}
              </code>
            ))}
            {seen.length > 24 && (
              <span className="text-[10px] text-app-muted">…+{seen.length - 24} more</span>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {/* Primary: accept the histogram-inferred type. */}
          <button
            type="button"
            onClick={() => onUseAsChoices(definition.suggestedType)}
            className="rounded-control border border-violet-500/60 bg-violet-500/25 px-2.5 py-1 text-[11px] font-semibold text-violet-100 transition hover:bg-violet-500/35"
          >
            Use as {TYPE_LABEL[definition.suggestedType]}
          </button>
          {/* Multi-select alternative: only when arrays were actually
              observed. Pure text/number/date rows shouldn't be teased
              into a dropdown when the data doesn't support it. */}
          {definition.suggestedType !== "multi_select" && (stats.multi_select ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => onUseAsChoices("multi_select")}
              className="rounded-control border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-violet-200 transition hover:bg-violet-500/20"
            >
              Use as Multi-select
            </button>
          )}
          {/* Single-select alternative: any string evidence is enough.
              Even a single-distinct-value text row could be promoted
              if the operator plans to add more choices later. Number /
              date / boolean rows never accumulate seen_values, so the
              button stays hidden for them. */}
          {definition.suggestedType !== "select" && seen.length >= 1 && (
            <button
              type="button"
              onClick={() => onUseAsChoices("select")}
              className="rounded-control border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-violet-200 transition hover:bg-violet-500/20"
            >
              Use as Single-select
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function PiiVisibleConfirmModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-card border border-border bg-app-panel p-6 shadow-card">
        <h2 className="text-lg font-semibold text-brand-red">Expose PII to the AI?</h2>
        <p className="mt-2 text-sm text-app-muted">
          PII fields visible to the AI must be carefully reviewed. The
          model will see the value verbatim in every system-prompt
          render. Confirm only if intended.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-control border border-border px-3 py-1.5 text-xs text-app-text hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-control bg-brand-red px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-redDeep"
          >
            Confirm — make AI-visible
          </button>
        </div>
      </div>
    </div>
  )
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-app-text">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-app-muted">{hint}</p>}
    </div>
  )
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className={cn("flex items-start justify-between gap-3", disabled && "opacity-60 cursor-not-allowed")}>
      <span className="space-y-0.5">
        <span className="block text-sm text-app-text">{label}</span>
        {hint && <span className="block text-[11px] text-app-muted">{hint}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 cursor-pointer accent-brand-red disabled:cursor-not-allowed"
      />
    </label>
  )
}
